<template>
	<div class="payments-settings">
		<AppCard :title="getString('payment_methods')" :button-text="getString('save')" @button-click="savePaymentMethods">
			<template v-slot:body>
				<AppList :items="this.items">
					<template #satispay-slot>
						<div class="page-container" v-if="userStore.currBusiness && userStore.currBusiness.satispay_status !== 1">
							<div class="page-explain">
								<p>{{ getString('satispay_explain') }}</p>
							</div>
							<div class="page-link">
								<AppTextInput ref="satispay-code" :label="getString('satispay_activation_code')" :placeholder="getString('example_satispay_activation_code')" />
								<AppButton class="mt-15" :text="getString('activate')" @click="activateSatispay" />
							</div>
						</div>
						<div class="satispay-link-complete" v-if="userStore.currBusiness && userStore.currBusiness.satispay_status === 1">
							<p>{{ getString('satispay_linked') }}</p>
						</div>
					</template>
					<template #voucherly-slot>
						<div class="page-container" v-if="userStore.currBusiness && (!userStore.currBusiness.voucherly_api_key)">
							<div class="page-explain">
								<p v-html="getString('voucherly_explain_link')"></p>
							</div>
							<div class="page-link">
								<AppTextInput :readonly="loadingVoucherly" ref="voucherly-key" :label="getString('voucherly_key')" :placeholder="getString('example_voucherly_key')" @pressed-enter="activateVoucherly" />
								<AppButton :loading="loadingVoucherly" class="mt-15" :text="getString('activate')" @click="activateVoucherly" />
							</div>
						</div>
						<div class="voucherly-link-complete" v-if="userStore.currBusiness && userStore.currBusiness.voucherly_api_key">
							<div class="col-12 align-end">
								<p>{{ getString('voucherly_linked') }}</p>
								<AppButton :paused="loadingVoucherly" class="mt-15" :text="getString('edit')" outline @click="userStore.currBusiness.voucherly_api_key = null" />
							</div>
						</div>
					</template>
					<template #numia-slot>
						<div class="page-container" v-if="userStore.currBusiness && (userStore.currBusiness.numia_status !== 1 || editingNumia)">
							<div class="page-explain">
								<p>{{ getString('numia_explain') }}</p>
							</div>
							<div class="page-link">
								<AppTextInput :readonly="loadingNumia" ref="numia-mer-id" :value="userStore.currBusiness.numia_mer_id || ''" :label="getString('numia_mer_id')" :placeholder="getString('example_numia_mer_id')" />
								<AppTextInput :readonly="loadingNumia" ref="numia-ksig" class="mt-15" no-autocomplete :label="getString('numia_ksig')" :placeholder="userStore.currBusiness.numia_ksig || getString('example_numia_ksig')" @pressed-enter="activateNumia" />
								<AppButton :loading="loadingNumia" class="mt-15" :text="getString('activate')" @click="activateNumia" />
							</div>
						</div>
						<div class="numia-link-complete" v-if="userStore.currBusiness && userStore.currBusiness.numia_status === 1 && !editingNumia">
							<div class="col-12 align-end">
								<p>{{ getString('numia_linked', [userStore.currBusiness.numia_mer_id]) }}</p>
								<AppButton class="mt-15" :text="getString('edit')" outline @click="editingNumia = true" />
							</div>
						</div>
					</template>
				</AppList>
			</template>
		</AppCard>
		<AppCard class="card-margin" :title="getString('payment_options')" :button-text="getString('save')" @button-click="saveOptions">
			<template v-slot:body>
				<AppList :items="this.itemsOptions" />
			</template>
		</AppCard>
		<AppPopup ref="popup-other-payments" :title="getString('enable_other_payments_methods')" :buttons="[{text: getString('cancel'), negative: true}, {text: getString('continue'), callback: subOtherPaymentsMethod}]">
			<div v-if="loadingOtherPayments" class="row centered loader-container"><AppLoader /></div>
			<p v-if="!loadingOtherPayments" class="popup-body-text">{{ getString('enable_other_payments_methods_explain') }}</p>
		</AppPopup>
		<CheckoutInPlace :client-secret="clientSecret" @toggling-popup="togglingPopupSheet" @checkout-complete="paymentComplete" />
		<AppPopup
			:title="getString('voucherly_key_error_sandbox_title')"
			:body-text="getString('voucherly_key_error_sandbox_text')"
			ref="voucherly-sandbox-error"
		/>
		<AppPopup
			:title="getString('voucherly_key_error_public_title')"
			:body-text="getString('voucherly_key_error_public_text')"
			ref="voucherly-public-error"
		/>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppList from "@/shared/components/AppList.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppPagination from "@/shared/components/AppPagination.vue";

import {useLanguageStore, useNotificationStore, useCurrencyStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import CheckoutInPlace from "@/console-new/components/shared/CheckoutInPlace.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, currencyStore, showNotification: notificationStore.showNotification, userStore}
	},
	name: "PaymentsSettingsContainer",
	components: {CheckoutInPlace, AppButton, AppTextInput, AppLoader, AppPopup, AppCard, AppList, AppPagination},
	data() {
		return {
			currOtherPaymentsPostExecution: null,
			loadingOtherPayments: false,
			shouldSaveHidePrices: false,
			loadingTest: false,
			loadingCreateStore: false,
			loadingVoucherly: false,
			loadingNumia: false,
			editingNumia: false,
			clientSecret: null,
			paramsAcceptedPayments: ["accept_cards", "accept_cash", "accept_satispay", "accept_klarna", "accept_voucherly", "accept_numia"]
		}
	},
	computed: {
		items() {
			let finalMethods = [
				{title: this.getString("cards_method"), switch: true, active: this.userStore.currBusiness && this.userStore.currBusiness.accept_cards, callback: val => this.togglePaymentMethod("cards", val)},
				{title: this.getString("cash"), switch: true, active: this.userStore.currBusiness && this.userStore.currBusiness.accept_cash, callback: val => this.togglePayedPaymentMethod('cash', val)},
			];
			if(this.userStore.currBusiness?.country === "IT") {
				finalMethods.push(
					{title: this.getString("satispay"), switch: true, active: this.userStore.currBusiness && this.userStore.currBusiness.accept_satispay, callback: val => this.togglePayedPaymentMethod('satispay', val), dropdownSlot: "satispay-slot"},
				)
			}
			if(this.userStore.currBusiness?.country === "IT") {
				finalMethods.push(
					{title: this.getString("voucherly"), switch: true, active: this.userStore.currBusiness && this.userStore.currBusiness.accept_voucherly, explain: this.getString("voucherly_explain"), callback: val => this.togglePayedPaymentMethod('voucherly', val), dropdownSlot: 'voucherly-slot'}
				)
			}
			if(this.userStore.currBusiness?.numia_enabled === 1) {
				finalMethods.push(
					{title: this.getString("numia_title"), switch: true, active: this.userStore.currBusiness.accept_numia, explain: this.getString("numia_method_explain"), callback: val => this.togglePaymentMethod("numia", val), dropdownSlot: 'numia-slot'}
				)
			}
			finalMethods.push(
				{title: this.getString("klarna"), switch: true, active: this.userStore.currBusiness && this.userStore.currBusiness.accept_klarna, explain: this.getString("klarna_explain"), callback: val => this.togglePaymentMethod("klarna", val)}
			)
			return finalMethods;
		},
		itemsOptions() {
			let minHidePricesAmount = this.currencyStore.readableCurrencyValue(this.currencyStore.currCurrency.min_hide_prices_amount);
			return [
				{title: this.getString("hide_prices"), switch: true, active: this.userStore.currBusiness && this.userStore.currBusiness.hide_prices, explain: this.getString("hide_prices_explain", [this.userStore.menuWord, minHidePricesAmount]), callback: this.toggleHidePrices},
			];
		},
	},
	methods: {
		togglingPopupSheet(v) {
			if(!v) {
				this.clientSecret = null;
			}
		},
		paymentComplete() {
			setTimeout(() => {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}, 2000);
			setTimeout(() => {
				this.clientSecret = null;
			}, 2500);
		},
		togglePaymentMethod(type, active) {
			let businessParam = 'accept_'+type;
			this.userStore.currBusiness[businessParam] = active;

			requestAnimationFrame(() => {
				if(!active) {
					if(!this.paramsAcceptedPayments.some(acceptedMethod => this.userStore.currBusiness[acceptedMethod])) {
						this.showNotification("at-least-one-payment-method");
						this.userStore.currBusiness[businessParam] = true;
					}
				}
			});
		},
		togglePayedPaymentMethod(type, active) {
			let businessParam = 'accept_'+type;
			this.userStore.currBusiness[businessParam] = active;

			requestAnimationFrame(() => {
				if(!active) {
					if(!this.paramsAcceptedPayments.some(acceptedMethod => this.userStore.currBusiness[acceptedMethod])) {
						this.showNotification("at-least-one-payment-method");
						this.userStore.currBusiness[businessParam] = true;
					}
				} else {
					if(!this.userStore.currBusiness.sub_otherpayments_id) {
						this.userStore.currBusiness[businessParam] = false;
						this.currOtherPaymentsPostExecution = type;
						this.$refs["popup-other-payments"].toggleStatus(true);
					}
				}
			});
		},
		subOtherPaymentsMethod() {
			if(!this.loadingOtherPayments) {
				this.loadingOtherPayments = true;
				this.userStore.subOtherPaymentsMethods(this.currOtherPaymentsPostExecution, (success, details) => {
					this.$refs["popup-other-payments"].toggleStatus(false);
					this.loadingOtherPayments = false;
					if(!success) {
						if(details && details === 'currency_mismatch') {
							this.showNotification("user-currency-mismatch", [this.userStore?.user?.name]);
						} else {
							this.showNotification("generic-fail");
						}
					} else {
						this.clientSecret = success;
					}
				});
			}
		},
		toggleHidePrices(active) {
			this.shouldSaveHidePrices = true;
			this.userStore.currBusiness.hide_prices = active;
		},
		savePaymentMethods() {
			let self = this;
			this.userStore.updatePaymentMethods(function (success, details) {
				if(success) {
					self.showNotification("generic-success");
				} else if(details === "no_methods_left") {
					self.showNotification("at-least-one-payment-method");
				} else {
					self.showNotification("generic-fail");
				}
			});
		},
		saveOptions() {
			let self = this;
			if(self.shouldSaveHidePrices) {
				self.userStore.updateHidePrices(function (success) {
					if(success) {
						self.showNotification("generic-success");
						self.shouldSaveHidePrices = false;
					} else {
						self.showNotification("generic-fail");
					}
				});
			} else {
				self.showNotification("generic-success");
			}
		},
		activateSatispay() {
			let code = this.$refs["satispay-code"].currentValue;
			if(code == null || code.length < 4) {
				this.showNotification("satispay-code-empty");
			} else {
				this.userStore.linkSatispay(code, (success, details) => {
					if(success) {
						this.showNotification("generic-success");
					} else {
						if(details === "token") {
							this.showNotification("satispay-code-invalid");
						} else {
							this.showNotification("generic-fail");
						}
					}
				});
			}
		},
		activateVoucherly() {
			if(this.loadingVoucherly) { return; }
			let code = this.$refs["voucherly-key"].currentValue;
			let regExp = /^\w+$/; // only letters and numbers and underscore
			if(!code?.length || code.length < 4) {
				this.showNotification("voucherly-code-empty");
			} else if(!regExp.test(code)) {
				this.showNotification("voucherly-code-invalid");
			} else {
				if((code.startsWith('pk_sand_') || code.startsWith('sk_sand_')) && !TEST) {
					this.$refs["voucherly-sandbox-error"].toggleStatus(true);
				} else if(code.startsWith('pk_live_')) {
					this.$refs["voucherly-public-error"].toggleStatus(true);
				} else if(!code.startsWith('sk_')) {
					this.showNotification("voucherly-code-invalid");
				} else {
					this.loadingVoucherly = true;
					this.userStore.linkVoucherly(code, (success, details) => {
						this.loadingVoucherly = false;
						if(success) {
							this.showNotification("generic-success");
						} else {
							let message = details === 'no_gateways' ? 'voucherly-no-gateways' : "voucherly-code-invalid";
							this.showNotification(message);
						}
					});
				}
			}
		},
		activateNumia() {
			if(this.loadingNumia) { return; }
			let merId = this.$refs["numia-mer-id"].currentValue?.trim();
			let ksig = this.$refs["numia-ksig"].currentValue?.trim() || "";
			// When credentials are already linked an empty KSIG keeps the stored one
			if(!merId || (!ksig && this.userStore.currBusiness.numia_status !== 1)) {
				this.showNotification("numia-fields-empty");
				return;
			}
			this.loadingNumia = true;
			this.userStore.linkNumia(merId, ksig, (success, details) => {
				this.loadingNumia = false;
				if(success) {
					this.editingNumia = false;
					this.showNotification("generic-success");
				} else if(details === "credentials") {
					this.showNotification("numia-credentials-invalid");
				} else if(details === "gateway_unreachable") {
					this.showNotification("numia-gateway-unreachable");
				} else {
					this.showNotification("generic-fail");
				}
			});
		}
	},
	watch: {
		"userStore.currBusinessId": {
			handler: function (val, oldVal) {
				if(val && val !== oldVal) {
					this.shouldSaveHidePrices = false;
					this.editingNumia = false;
				}
			},
			deep: true
		}
	}
}
</script>

<style lang="scss" scoped>
.payments-settings {

	:deep(.card-container .card-body) {
		padding: 0;
		& > .list-container {
			.list-item:first-child::before {
				display: none;
			}

			.list-item:last-child::after {
				display: none;
			}

		}
	}

	.extra-text {
		p {
			font-size: 14px;
			color: #9B9B9B;
		}
	}

	.loader-container {
		padding: 20px 0;
	}

	.satispay-link-complete, .voucherly-link-complete, .numia-link-complete {
		display: flex;
		justify-content: flex-end;

		p {
			font-size: 14px;
		}
	}

	.voucherly-link-complete, .numia-link-complete {
		align-items: center;
		gap: 10px;
	}

	.page-container {
		display: flex;
		width: 100%;
		justify-content: space-between;

		.page-explain {
			padding-right: 10px;

			p {
				font-size: 14px;
				color: #9B9B9B;
			}
		}

		.page-link {
			display: flex;
			flex-direction: column;
			align-items: flex-end;

			.input-wrapper {
				width: 220px;

				@media(max-width: 500px) {
					width: 140px;
				}

				@media(max-width: 400px) {
					width: 120px;
				}
			}
		}
	}

	.card-margin {
		margin-top: var(--main-padding);
	}

}

.popup-body-text {
	text-align: center;
}
</style>